import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form, FormGroup, Label, Input, Button, Card, CardBody, Row, Col,
  Collapse, CardHeader, Badge,
  Toast
} from 'reactstrap';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import JuniorHandlingResultsAttendee from './JuniorHandlingResultsAttendee';
import { getJuniorHandlingFileResults, getJuniorHandlingAttendeeResults } from '../../../store/juniorHandlingResult/actions';
import file_result_banner from '../../../assets/file_result_banner.png';
import { CircularProgress, Select } from '@mui/material';
import { toast } from 'react-toastify';

// Small debounce helper
const useDebounce = (value, delay = 200) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
};

function JuniorHandlingFileResults() {
  const dispatch = useDispatch();
  const { fileResultRes, loadingGetFileResults, count, filterOptions, jrHandleAttendee, loadingJrHandleAttendee } = useSelector(state => state.JuniorHandlingDisplayReducer);


  const clubSearchRef = useRef(null);
  const [showClubOptions, setShowClubOptions] = useState(false);

  const [firstLoadDone, setFirstLoadDone] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const limit = 15;

  const [allEvents, setAllEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [loadedAttendees, setLoadedAttendees] = useState({});

  const urlParams = new URLSearchParams(window.location.search);

  // Get values from URL or use defaults
  const eventCategory = urlParams.get('event_category') || 'Junior Handling (Conformation)';
  const startYear = urlParams.get('start_year') || new Date().getFullYear();


  const [filters, setFilters] = useState({
    event_category: eventCategory,
    start_year: startYear,
    province: '',
    club_name: '',
    event_number: '',
    //junior_handler: '',

  });

  const [activeFilters, setActiveFilters] = useState({
    event_category: true,
    start_year: true,
    province: false,
    club_name: false,
    event_number: false,
    //junior_handler: false,

  });

  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const [searchInputs, setSearchInputs] = useState({
    club_name: '',
    event_number: '',
    //junior_handler: '',
    province: '',
  });

  const [initialOptionsMap, setInitialOptionsMap] = useState({}); // { "Junior Handling (Conformation)_2025": optionsObj, ... }
  const inputRefs = useRef({}); // refs for autofocus

  useEffect(() => {
    if (!filterOptions || typeof filterOptions !== "object") return;
    const key = `${filters.event_category}_${filters.start_year}`;

    const cleaned = Object.entries(filterOptions).reduce((acc, [k, v]) => {
      if (Array.isArray(v) && v.length > 0) {
        acc[k] = [...new Set(v.map((x) => x?.trim?.()))].sort();
      }
      return acc;
    }, {});

    if (Object.keys(cleaned).length === 0) return;

    setInitialOptionsMap((prev) => {
      const existing = prev[key];
      if (existing) {
        const isCached = Object.keys(cleaned).every(
          (k) => existing[k]?.length >= cleaned[k]?.length
        );
        if (isCached) return prev;
      }
      return { ...prev, [key]: { ...(existing || {}), ...cleaned } };
    });
  }, [filterOptions, filters.event_category, filters.start_year]);

  const clubOptions = useMemo(() => {
    const clubs = new Set(
      fileResultRes?.flat()
        ?.filter(group => group.events_by_number?.some(eventBlock =>
          eventBlock?.events?.some(event => event.event_category === filters.event_category)
        ))
        .map(group => group.club)
        ?.filter(club => club) // Remove null/undefined
    );
    return Array.from(clubs).sort();
  }, [fileResultRes, filters.event_category]);

  // Club search states
  const [clubSearch, setClubSearch] = useState("");

  // Fast search
  const filteredClubOptions = useMemo(() => {
    if (!clubSearch) return clubOptions;
    return clubOptions?.filter(club =>
      club?.toLowerCase().includes(clubSearch?.toLowerCase())
    );
  }, [clubOptions, clubSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {

      if (clubSearchRef.current && !clubSearchRef.current.contains(e.target)) {

        setShowClubOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const debouncedClub = useDebounce(searchInputs.club_name, 100);
  const debouncedEventNumber = useDebounce(searchInputs.event_number, 100);
  const debouncedDog = useDebounce(searchInputs.junior_handler, 100);


  // Group them into an object (safe & stable)
  const debouncedSearch = useMemo(() => ({
    club_name: debouncedClub,
    event_number: debouncedEventNumber,
    //junior_handler: debouncedDog,

  }), [debouncedClub, debouncedEventNumber, debouncedDog]);

  const optionsSnapshot = useMemo(() => {
    const key = `${filters.event_category}_${filters.start_year}`;
    return initialOptionsMap[key] || {
      clubs: [],
      eventNumbers: [],
      //juniorHandler: [],
      provinces: []
    };
  }, [initialOptionsMap, filters.event_category, filters.start_year]);


  useEffect(() => {
    if (!fileResultRes) return;
    if (fileResultRes && !isFetchingMore) {
      // first load (replace)
      setAllEvents(fileResultRes?.flat());
      setFirstLoadDone(true);

      // Reset loaded attendees when new search is performed
      setLoadedAttendees({});
      setExpandedEvents({});
    } else if (fileResultRes?.flat() && isFetchingMore) {
      // append mode
      setAllEvents(prev => [...prev, ...fileResultRes.flat()]);
      setIsFetchingMore(false);
    }
  }, [fileResultRes]);

  // 🔹 Handle accordion expand/collapse and load attendees
  const handleAccordionExpand = async (club, eventNumber, eventBlock) => {
    const key = `${club}_${eventNumber}`;

    // If already expanded, just collapse
    if (expandedEvents[key]) {
      setExpandedEvents(prev => ({ ...prev, [key]: false }));
      return;
    }

    // If not loaded, fetch attendees data
    try {
      setExpandedEvents(prev => ({ ...prev, [key]: true }));


      // Check if we already have this data cached
      if (!loadedAttendees[key]) {
        dispatch(getJuniorHandlingAttendeeResults({
          ...filters,
          club: club,
          event_number: eventNumber,
          event_category: filters.event_category,
          start_year: filters.start_year,
        }));
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
      setExpandedEvents(prev => ({ ...prev, [key]: false }));
      toast.error('Failed to load event details');
    }
  };


  useEffect(() => {
    if (jrHandleAttendee && !loadingJrHandleAttendee) {
      // Extract the key from the response data
      if (jrHandleAttendee?._id?.club && jrHandleAttendee?._id?.event_number) {
        const eventKey = `${jrHandleAttendee?._id?.club}_${jrHandleAttendee?._id?.event_number}`;

        // Cache the data if this event is currently expanded
        if (expandedEvents[eventKey] && !loadedAttendees[eventKey]) {
          setLoadedAttendees(prev => ({
            ...prev,
            [eventKey]: jrHandleAttendee
          }));
        }
      }
    }
  }, [jrHandleAttendee, loadingJrHandleAttendee, expandedEvents]);


  // 🔹 Filtered options after search input
  const filteredOptions = useCallback((key, list) => {
    const term = debouncedSearch[key]?.toLowerCase();
    if (!term) return list;
    return list?.filter(opt => opt && opt.toLowerCase().includes(term));
  }, [debouncedSearch]);

  // 🔹 Handle fetch more
  const fetchMore = () => {
    if (loadingGetFileResults || isFetchingMore || !hasMore) return;
    const nextPage = page + 1;
    const skip = nextPage * limit;

    if (count && skip >= count) {
      setHasMore(false);
      setIsFetchingMore(false);
      return;
    }

    setIsFetchingMore(true);
    dispatch(
      getJuniorHandlingFileResults({
        ...filters,
        skip,
        limit,
      })
    );
    setPage(nextPage);
  };


  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Get values from URL or use defaults
    const eventCategory = urlParams.get('event_category') || 'Junior Handling (Conformation)';
    const startYear = urlParams.get('start_year') || new Date().getFullYear();

    // ✅ Fetch only once on mount
    dispatch(getJuniorHandlingFileResults({
      event_category: eventCategory,
      start_year: startYear
    }));
  }, [dispatch]);





  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    // 🔹 Special: reset all filters when category changes
    if (name === "event_category") {
      setFilters({
        event_category: value,
        start_year: new Date().getFullYear(),
        province: '',
        club_name: '',
        event_number: '',
        //junior_handler: '',

      });
      setActiveFilters({
        event_category: true,
        start_year: true,
        province: false,
        club_name: false,
        event_number: false,
        //junior_handler: false,
      });
      setSearchInputs({
        club_name: '',
        event_number: '',
        // junior_handler: '',
        province: '',
      });
    } else {
      setFilters({ ...filters, [name]: value });
    }
  };
  const flattenedData = fileResultRes?.flat() || [];




  const handleSearchChange = (key, value) => {
    setSearchInputs(prev => ({ ...prev, [key]: value }));
    setShowClubOptions(true); // Show only when typing
  };

  const handleSelect = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSearchInputs(prev => ({ ...prev, [key]: value })); // clear search
    setShowClubOptions(false); // Show only when typing
  };

  const handleFilterToggle = (filterName) => {
    const activeCount = Object.values(activeFilters)?.filter(Boolean)?.length;
    if (activeFilters[filterName] && activeCount === 1) {
      toast.error("At least one filter must remain active");
      return;
    }
    setActiveFilters((prev) => {
      const isActivating = !prev[filterName];
      const updated = { ...prev, [filterName]: isActivating };

      if (isActivating) {
        // If enabling → focus input & start blank
        setSearchInputs((p) => ({ ...p, [filterName]: "" }));
        setFilters((p) => ({ ...p, [filterName]: "" }));
        setTimeout(() => inputRefs.current[filterName]?.focus(), 100);
      } else {
        // If disabling → clear stored values
        setSearchInputs((p) => ({ ...p, [filterName]: "" }));
        setFilters((p) => ({ ...p, [filterName]: "" }));
      }

      return updated;
    });
    if (!activeFilters[filterName]) setFilters(prev => ({ ...prev, [filterName]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const activeOnlyFilters = Object.keys(filters).reduce((acc, key) => {
      if (activeFilters[key]) acc[key] = filters[key];
      return acc;
    }, {});
    setAllEvents([]);
    setPage(0);
    setHasMore(true);
    setLoadedAttendees({});
    setExpandedEvents({});
    dispatch(getJuniorHandlingFileResults({
      ...activeOnlyFilters,
      skip: 0,
      limit,
    }));
  };

  // 🔹 Infinite Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (
        !firstLoadDone || // ✅ Prevent before first API
        loadingGetFileResults ||
        isFetchingMore ||
        !hasMore ||
        allEvents.length === 0
      ) return;

      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 400 &&
        hasMore &&
        !loadingGetFileResults
      ) {
        fetchMore();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [firstLoadDone, hasMore, loadingGetFileResults, isFetchingMore, allEvents]);

  const handleReset = () => {
    setFilters({
      event_category: 'Junior Handling (Conformation)',
      start_year: new Date().getFullYear(),
      province: '',
      club_name: '',
      event_number: '',
      //junior_handler: '',
    });
    setActiveFilters({
      event_category: true,
      start_year: true,
      province: false,
      club_name: false,
      event_number: false,
      //junior_handler: false,
    });
    setSearchInputs({
      club_name: '',
      event_number: '',
      //junior_handler: '',
      province: '',
    });
    setAllEvents([]);
    setPage(0);
    setHasMore(true);
    setLoadedAttendees({});
    setExpandedEvents({});

    dispatch(getJuniorHandlingFileResults({
      event_category: 'Junior Handling (Conformation)', start_year: new Date().getFullYear(), skip: 0,
      limit,
    }));
  };

  const getActiveFilterCount = () => Object.values(activeFilters)?.filter(Boolean)?.length;

  // 🔹 Render input with autocomplete dropdown
  const renderSearchableInput = (label, key, list) => (
    <FormGroup className="mb-1 position-relative">
      <Label for={key} className="fw-bold">{label}</Label>
      <Input
        type="text"
        id={key}
        value={searchInputs[key] || ""}
        onChange={(e) => {
          handleSearchChange(key, e.target.value)
          const value = (e.target.value)?.trim();
          setFilters(prev => ({                    // also update actual filters
            ...prev,
            [key]: value
          }));

          setShowClubOptions(true);

        }}
        placeholder={`Type to search ${label.toLowerCase()}...`}
        className="border-primary"
        autoComplete="off"
      />
      {(searchInputs[key] || filters[key]) && (
        <span
          style={{
            position: 'absolute',
            right: 10,
            top: '70%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            fontWeight: 'bold',
            color: '#6d7fcc',
          }}
          onClick={() => {
            setSearchInputs(prev => ({ ...prev, [key]: '' }));
            setFilters(prev => ({ ...prev, [key]: '' }));
            setShowClubOptions(false); // hide options
          }}
        >
          ×
        </span>
      )}
      {showClubOptions && searchInputs[key] && filteredOptions(key, list)?.length > 0 && (
        <ul className="list-group position-absolute w-100 mt-1 shadow-sm" style={{ zIndex: 1000 }}>
          {filteredOptions(key, list).map((opt, idx) => (
            <li
              key={idx}
              className="list-group-item list-group-item-action"
              style={{ cursor: "pointer" }}
              onClick={() => handleSelect(key, opt)}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </FormGroup>
  );



  return (
    <div className="event-results-container ">
      {/* <img src={file_result_banner} class="file_result_banner" /> */}
      {/* Header Section */}
      <Card className="mb-3 shadow-sm">
        <CardBody className="bg-light">
          <Row className="align-items-center">
            <Col md={8}>
              <h2 style={{ color: "#6d7fcc" }} className="mb-1">
                <FilterListIcon className="me-2" />
                Junior Handling Results
              </h2>
              <p className="text-muted mb-0">
                Search and filter through junior handling results with advanced options
              </p>
            </Col>
            <Col md={4} className="text-end">
              <Badge
                pill
                className="me-2 fs-6"
                style={{
                  cursor: 'pointer',
                  backgroundColor: '#6d7fcc', // red-200 and secondary
                  // Ensure text contrast
                  border: 'none' // Optional: remove border if needed
                }}
              >
                {count || 0} Events Found
              </Badge>
              <Badge color="secondary" pill className="fs-6">
                {getActiveFilterCount()} Filters Active
              </Badge>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Filter Selection Card */}
      <Card className="mb-3 shadow-sm">
        <CardHeader
          className="bg-white cursor-pointer"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          style={{ cursor: 'pointer' }}
        >
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">
                <FilterListIcon className="me-2" />
                Filter Options
              </h5>
            </Col>
            <Col xs="auto">
              <Badge color="" className="me-2 " style={{
                cursor: 'pointer',
                backgroundColor: '#6d7fcc', // red-200 and secondary
                // Ensure text contrast
                border: 'none' // Optional: remove border if needed
              }}>
                {getActiveFilterCount()} Active
              </Badge>
              <Button
                size="sm"
                color="link"
                className="p-0"
              >
                {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </Col>
          </Row>
        </CardHeader>

        <Collapse isOpen={isFilterOpen}>
          <CardBody>
            {/* Filter Category Selection */}
            <Row className="mb-3">
              <Col>
                <h6 className="text-muted mb-3">Select Filter Categories:</h6>
                <div className="d-flex flex-wrap gap-2">
                  {Object.keys(activeFilters).map(filterKey => (
                    <Badge
                      key={filterKey}
                      color={""}
                      className="cursor-pointer px-3 py-2"
                      onClick={() => handleFilterToggle(filterKey)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: activeFilters[filterKey] ? '#6d7fcc' : '#6c757d', // red-200 and secondary
                        color: activeFilters[filterKey] ? '#fff' : '#fff', // Ensure text contrast
                        border: 'none' // Optional: remove border if needed
                      }}
                    >
                      {filterKey.split('_').map(word =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </Badge>
                  ))}
                </div>
              </Col>
            </Row>

            <Form onSubmit={handleSubmit} innerRef={clubSearchRef}>
              <Row>
                {activeFilters.event_category && (
                  <Col md={4}>
                    <FormGroup>
                      <Label className="fw-bold">Event Category *</Label>
                      <Input
                        type="select"
                        name="event_category"
                        value={filters.event_category}
                        onChange={handleFilterChange}
                        className="border-primary"
                      >
                        <option value="Junior Handling (Conformation)">Junior Handling (Conformation)</option>
                        <option value="Junior Handling (Obedience)">Junior Handling (Obedience)</option>

                      </Input>
                    </FormGroup>
                  </Col>
                )}

                {activeFilters.start_year && (
                  <Col md={4}>
                    <FormGroup>
                      <Label className="fw-bold">Select Year</Label>
                      <Input
                        type="select"
                        name="start_year"
                        value={filters.start_year}
                        onChange={handleFilterChange}
                        className="border-primary"
                      >
                        {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => 2024 + i)
                          .reverse()
                          .map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                      </Input>
                    </FormGroup>
                  </Col>
                )}

                {activeFilters.province && (
                  <Col md={4}>
                    <FormGroup>
                      <Label className="fw-bold">Province</Label>
                      <Input
                        type="select"
                        name="province"
                        value={filters.province}
                        onChange={handleFilterChange}
                        className="border-primary"
                      >
                        <option value="">Select a province</option>
                        <option value="Alberta">Alberta</option>
                        <option value="British Columbia">British Columbia</option>
                        <option value="Manitoba">Manitoba</option>
                        <option value="New Brunswick">New Brunswick</option>
                        <option value="Newfoundland">Newfoundland</option>
                        <option value="Nova Scotia">Nova Scotia</option>
                        <option value="Ontario">Ontario</option>
                        <option value="Prince Edward Island">Prince Edward Island</option>
                        <option value="Quebec">Quebec</option>
                        <option value="Saskatchewan">Saskatchewan</option>
                        <option value="Yukon">Yukon</option>
                      </Input>
                    </FormGroup>
                  </Col>
                )}


                {activeFilters.club_name && (
                  <Col md={4}>{renderSearchableInput("Club Name", "club_name", optionsSnapshot.clubs)}</Col>
                )}

                {activeFilters.event_number && (
                  <Col md={4}>{renderSearchableInput("Event Number", "event_number", optionsSnapshot.eventNumbers)}</Col>
                )}

                {/* {activeFilters.junior_handler && (
                  <Col md={4}>{renderSearchableInput("Junior Handler", "junior_handler", optionsSnapshot.juniorHandler)}</Col>
                )} */}

                {activeFilters.judge && (
                  <Col md={4}>{renderSearchableInput("Judge", "judge", optionsSnapshot.judges)}</Col>
                )}
              </Row>

              {/* Buttons */}
              <Row className="mt-3">
                <Col className="d-flex gap-2 justify-content-start">
                  <Button style={{
                    cursor: 'pointer',
                    backgroundColor: '#6d7fcc', // red-200 and secondary
                    // Ensure text contrast
                    border: 'none' // Optional: remove border if needed
                  }}
                    type="button" onClick={handleReset} disabled={getActiveFilterCount() === 0}>
                    Reset Filters
                  </Button>
                  <Button type="submit"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: '#6d7fcc', // red-200 and secondary
                      // Ensure text contrast
                      border: 'none' // Optional: remove border if needed
                    }}
                    disabled={loadingGetFileResults}>
                    {loadingGetFileResults ? "Searching..." : "Search"}
                  </Button>
                </Col>
              </Row>
            </Form>

          </CardBody>
        </Collapse>
      </Card>

      {/* Results Section */}
      {(loadingGetFileResults && allEvents?.length === 0) ? (
        <Card className="text-center py-5">
          <CardBody>
            <div style={{

              color: "#6d7fcc",

            }} className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 mb-0">Loading junior handling results...</p>
          </CardBody>
        </Card>
      ) : allEvents?.length > 0 ? (
        <>
          {allEvents?.map((group, index) => {
            const validEventBlocks = group.events_by_number?.filter(
              eventBlock => eventBlock?.event_number && eventBlock?.event_count > 0
            );

            if (!validEventBlocks || validEventBlocks?.length === 0) {
              return null;
            }
            //if(group?.events_by_number[0]?.events[0]?.attendees?.length === 0) return null;
            const startStr = group.formatted_start_date;
            const endStr = group.formatted_end_date;
            const dateRange = startStr === endStr ? startStr : `${startStr} to ${endStr}`;
            const city = group.venue_city || group.venue || '';
            const location = city ? `${city}, ${group.province}` : group.province;


            return (
              <Accordion key={index} className="mb-3 shadow-sm" style={{
                transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                backgroundColor: "#fff"
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = "#eef1fb";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(109, 127, 204, 0.2)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = "#fff";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" component="div" className="fw-semibold ">
                    <span className="" style={{
                      cursor: 'pointer',
                      color: "#6d7fcc",
                      border: 'none',
                      fontSize: "1rem"
                    }}>{group?.club}</span>
                    <small className="text-muted ms-3" style={{ fontSize: ".900rem" }}>
                      {dateRange} {location ? `• ${location}` : ''}
                    </small>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Show Trial → Event Numbers */}
                  {group?.events_by_number?.filter(eventBlock => eventBlock?.event_number && eventBlock?.event_count > 0)?.map((eventBlock, idx) => {

                    let validData = eventBlock.events?.filter((data, index) => {
                      return data.has_qualifiers === true
                    });


                    if (validData?.length === 0) return;
                    let eventDate = new Date(eventBlock?.startDate).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                        timeZone: "UTC"
                    
                    });

                    const eventKey = `${group?.club}_${eventBlock?.event_number}`;
                    const isExpanded = expandedEvents[eventKey] || false;
                    const hasLoadedAttendees = loadedAttendees[eventKey];
                    const isThisEventLoading = isExpanded && !hasLoadedAttendees && loadingJrHandleAttendee;
                    return (
                      <Accordion key={idx} expanded={isExpanded}
                        className="mb-2"
                        onChange={() => handleAccordionExpand(group.club, eventBlock.event_number, eventBlock)}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1" className="fw-bold">
                            {(eventBlock?.event_number) && `${eventDate || startStr} | Event #${eventBlock?.event_number}`}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {/* Pass events of this event number */}
                          {/* <JuniorHandlingResultsAttendee data={eventBlock?.events} /> */}
                          {isThisEventLoading ? (
                            <div className="text-center p-3">
                              <CircularProgress size={24} style={{ color: '#6d7fcc' }} />
                              <p className="mt-2 mb-0">Loading attendees...</p>
                            </div>
                          ) : hasLoadedAttendees ? (
                            <JuniorHandlingResultsAttendee data={hasLoadedAttendees.events || []} />
                          ) : (
                            <div className="text-center p-3 text-muted">

                            </div>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )
                  })}
                </AccordionDetails>
              </Accordion>
            )
          })}

          {isFetchingMore && (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" />
              <p>Loading more results ...</p>
            </div>
          )}

        </>
      ) : (
        <Card className="text-center py-5">
          <CardBody>
            <FilterListIcon className="text-muted mb-3" style={{ fontSize: '3rem' }} />
            <h5 className="text-muted">No events found</h5>
            <p className="text-muted">Try adjusting your filters to see more results.</p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

export default JuniorHandlingFileResults;
